import "./../../styles.css";
import { useQuery } from "@tanstack/react-query";
import { Row, Col, Tag, Card, Divider } from "antd";
import { WarningOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getVehicleHealthStatus } from "apis/vehicleHealthApis";
import { getVehicleOverview } from "apis/vehicleAnalyticsApis";
import { VEHICLE_ANALYTICS } from "components/Common/svgIconComponents";
import VehicleImageWidget from "components/Common/Widgets/VehicleImageWidget";
import VehicleHealthWidget from "components/Common/Widgets/VehicleHealthWidget";
import Loading from "components/Common/Loading";
import APIError from "components/Common/APIError";
import { Link } from "react-router-dom";
import { ROUTES, UNITS } from "components/Common/Constants";

const tripsTitle = (
  <div className="vaCardTitle">
    <VEHICLE_ANALYTICS.TRIPS />
    <span>Trips</span>
  </div>
);
const chargingTitle = (
  <div className="vaCardTitle">
    <VEHICLE_ANALYTICS.CHARGING />
    <span>Charging Cycles</span>
  </div>
);
const vehicleHealthTitle = (
  <div className="vaCardTitle">
    <VEHICLE_ANALYTICS.HEALTH />
    <span>Vehicle Health</span>
  </div>
);
const alertsTitle = (
  <div className="vaCardTitle">
    <VEHICLE_ANALYTICS.ALERTS />
    <span>Threshold Alerts</span>
  </div>
);
const alarmsTitle = (
  <div className="vaCardTitle">
    <VEHICLE_ANALYTICS.ALARMS />
    <span>System Alarms</span>
  </div>
);
const notificationsTitle = (
  <div className="vaCardTitle">
    <VEHICLE_ANALYTICS.NOTFICATION />
    <span>Notifications</span>
  </div>
);

const Overview = ({ selectedTab }) => {
  const eol = useSelector((state) => state.eol);
  const vehicleDetails = eol?.vehicleDetails;

  const {
    data: vehicleHealthData,
    isLoading: vehicleHealthLoading,
    isError: vehicleHealthIsError,
    error: vehicleHealthError,
  } = useQuery({
    queryKey: ["vehicleHealth", vehicleDetails?.vehImei],
    queryFn: () => getVehicleHealthStatus(vehicleDetails?.vehImei),
    keepPreviousData: true,
    enabled: !!(vehicleDetails?.vehImei && selectedTab === "overview"),
    staleTime: 1 * 10 * 60 * 1000,
  });

  const {
    data: vehicleOverviewData,
    isLoading: vehicleOverviewLoading,
    isError: vehicleOverviewIsError,
    error: vehicleOverviewError,
  } = useQuery({
    queryKey: ["vehicleOverview", vehicleDetails?.vehImei],
    queryFn: () => getVehicleOverview(vehicleDetails?.vehImei),
    keepPreviousData: true,
    enabled: !!(vehicleDetails?.vehImei && selectedTab === "overview"),
    staleTime: 1 * 10 * 60 * 1000,
  });

  return (
    <div className="vehicle-analytics-overview">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={{ span: 12, order: 1 }} lg={{ span: 6, order: 1 }}>
              <Card className="vaCards" style={{ height: "100%" }}>
                <Row>
                  <Col span={24}>
                    {!vehicleDetails && <Loading />}
                    {vehicleDetails && (
                      <VehicleImageWidget
                        vehicleDetails={vehicleDetails}
                        showDetails={true}
                        showOdometer={true}
                      />
                    )}
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col
              xs={24}
              md={{ span: 24, order: 3 }}
              lg={{ span: 12, order: 2 }}
            >
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Card
                    className="vaCards"
                    title={tripsTitle}
                    extra={<InfoCircleOutlined />}
                  >
                    {vehicleOverviewLoading && <Loading />}
                    {vehicleOverviewIsError && (
                      <APIError message={vehicleOverviewError.message} />
                    )}
                    {!vehicleOverviewIsError && (
                      <Row>
                        <Col
                          span={24}
                          style={{
                            alignContent: "center",
                            textAlign: "center",
                          }}
                        >
                          <Row>
                            <Col xs={11} sm={5}>
                              <div>
                                <p style={{ fontWeight: 600 }}>Count</p>
                                {vehicleOverviewData?.tripCount !== null ?
                                <p>{vehicleOverviewData?.tripCount}</p>
                                : <p className="no-data-text">NA</p>}
                              </div>
                            </Col>
                            <Col span={1}>
                              <Divider type="vertical" />
                            </Col>
                            <Col xs={11} sm={5}>
                              <div>
                                <p style={{ fontWeight: 600 }}>Distance</p>
                                {vehicleOverviewData?.distanceTravelled !== null ?
                                <p>{vehicleOverviewData?.distanceTravelled} {UNITS.KM}</p>
                                : <p className="no-data-text">NA</p>}
                              </div>
                            </Col>
                            <Col xs={0} sm={1}>
                              <div>
                                <Divider type="vertical" />
                              </div>
                            </Col>
                            <Col xs={11} sm={5}>
                              <div>
                                <p style={{ fontWeight: 600 }}>Range</p>
                                  {vehicleOverviewData?.range !== null
                                    ? <p>{vehicleOverviewData?.range}</p>
                                    : <p className="no-data-text">NA</p>}
                              </div>
                            </Col>
                            <Col span={1}>
                              <Divider type="vertical" />
                            </Col>
                            <Col xs={11} sm={5}>
                              <div>
                                <p style={{ fontWeight: 600 }}>Top Speed</p>
                                {vehicleOverviewData?.topSpeed !== null ?
                                <p>{vehicleOverviewData?.topSpeed} {UNITS.KM_PER_HR}</p>
                                : <p className="no-data-text">NA</p>}
                              </div>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    )}
                  </Card>
                </Col>
                <Col span={24}>
                  <Card
                    className="vaCards"
                    title={chargingTitle}
                    extra={<InfoCircleOutlined />}
                  >
                    {vehicleOverviewLoading && <Loading />}
                    {vehicleOverviewIsError && (
                      <APIError message={vehicleOverviewError.message} />
                    )}
                    {!vehicleOverviewIsError && (
                      <Row style={{ minHeight: "90px" }}>
                        <Col
                          span={24}
                          style={{
                            alignContent: "center",
                            textAlign: "center",
                          }}
                        >
                          <Row>
                            <Col span={11}>
                              <div>
                                <p style={{ fontWeight: 600 }}>
                                  Total Discharge Cycles
                                </p>
                                <p>
                                  {vehicleOverviewData?.totalDischargeCycles || <span className="no-data-text">NA</span>}
                                </p>
                              </div>
                            </Col>
                            <Col span={1}>
                              <Divider type="vertical" />
                            </Col>
                            <Col span={11}>
                              <div>
                                <p style={{ fontWeight: 600 }}>
                                  Avg time to full Charge
                                </p>
                                <p className="no-data-text">{"---"}</p>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    )}
                  </Card>
                </Col>
              </Row>
            </Col>
            <Col xs={24} md={{ span: 12, order: 2 }} lg={{ span: 6, order: 3 }}>
              <Card
                className="vaCards"
                title={vehicleHealthTitle}
                style={{ height: "100%" }}
                extra={<InfoCircleOutlined />}
              >
                {vehicleHealthLoading && <Loading />}
                {vehicleHealthIsError && (
                  <APIError message={vehicleHealthError?.message} />
                )}
                {!vehicleHealthLoading && vehicleHealthData && (
                  <VehicleHealthWidget
                    vehiclePartList={vehicleHealthData?.parts}
                    vehicleDetails={vehicleDetails}
                  />
                )}
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row gutter={[16, 16]} className="mt05">
        <Col span={24} style={{ padding: "10px" }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={{ span: 12, order: 1 }} lg={{ span: 6, order: 1 }}>
              <Card
                className="vaCards"
                title={alarmsTitle}
                extra={<InfoCircleOutlined />}
              >
                {vehicleOverviewLoading && <Loading />}
                {vehicleOverviewIsError && (
                  <APIError message={vehicleOverviewError.message} />
                )}
                {!vehicleOverviewIsError && (
                  <>
                    <Row gutter={[16, 16]}>
                      <Col
                        span={12}
                        style={{ alignContent: "center", textAlign: "center" }}
                      >
                        {"Motor Alarms"}
                      </Col>
                      <Col
                        span={12}
                        style={{ alignContent: "center", textAlign: "center" }}
                      >
                        {"Battery Alarms"}
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        span={12}
                        style={{ alignContent: "center", textAlign: "center" }}
                      >
                          {vehicleOverviewData?.totalMotorAlarms !== null
                            ? <p>{vehicleOverviewData?.totalMotorAlarms}</p>
                            : <p className="no-data-text"> NA </p>}
                      </Col>
                      <Col
                        span={12}
                        style={{ alignContent: "center", textAlign: "center" }}
                      >
                          {vehicleOverviewData?.totalBatteryAlarms !== null
                            ? <p>{vehicleOverviewData?.totalBatteryAlarms}</p>
                            : <p className="no-data-text"> NA </p>}
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        span={24}
                      >
                        <Link to={ROUTES.ALERTS.link} state={{ tabName: "systemAlarms", vehicleDetails }} >
                          <div className="cursor-pointer view-more-link flex-center">More</div>
                        </Link>
                      </Col>
                    </Row>
                  </>
                )}
              </Card>
            </Col>
            <Col
              xs={24}
              md={{ span: 24, order: 3 }}
              lg={{ span: 12, order: 2 }}
            >
              <Card
                className="vaCards"
                title={alertsTitle}
                extra={<InfoCircleOutlined />}
              >
                {vehicleOverviewLoading && <Loading />}
                {vehicleOverviewIsError && (
                  <APIError message={vehicleOverviewError.message} />
                )}
                {!vehicleOverviewIsError && (
                  <>
                    <Row>
                      <Col
                        span={8}
                        style={{ alignContent: "center", textAlign: "center" }}
                      >
                        <Tag color={"error"} icon={<WarningOutlined />}>
                          {"Critical"}{" "}
                        </Tag>
                      </Col>
                      <Col
                        span={8}
                        style={{ alignContent: "center", textAlign: "center" }}
                      >
                        <Tag color={"warning"} icon={<InfoCircleOutlined />}>
                          {"Warning"}{" "}
                        </Tag>
                      </Col>
                      <Col
                        span={8}
                        style={{ alignContent: "center", textAlign: "center" }}
                      >
                        <Tag color={"processing"} icon={<InfoCircleOutlined />}>
                          {"Information"}{" "}
                        </Tag>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        span={8}
                        style={{ alignContent: "center", textAlign: "center" }}
                      >
                        {vehicleOverviewData?.totalCriticalAlerts !== null
                        ? <p>{vehicleOverviewData?.totalCriticalAlerts}</p>
                        : <p className="no-data-text">NA</p>}
                      </Col>
                      <Col
                        span={8}
                        style={{ alignContent: "center", textAlign: "center" }}
                      >
                        {vehicleOverviewData?.totalWarningAlerts !== null
                        ? <p>{vehicleOverviewData?.totalWarningAlerts}</p>
                        : <p className="no-data-text">NA</p>}
                      </Col>
                      <Col
                        span={8}
                        style={{ alignContent: "center", textAlign: "center" }}
                      >
                        {vehicleOverviewData?.totalInformationalAlerts !== null
                        ? <p>{vehicleOverviewData?.totalInformationalAlerts}</p>
                        : <p className="no-data-text">NA</p>}
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        span={24}
                      >
                        <Link to={ROUTES.ALERTS.link} state={{ tabName: "deviationMonitor", vehicleDetails }} >
                          <div className="view-more-link cursor-pointer flex-center">More</div>
                        </Link>
                      </Col>
                    </Row>
                  </>
                )}
              </Card>
            </Col>
            <Col xs={24} md={{ span: 12, order: 2 }} lg={{ span: 6, order: 3 }}>
              <Card
                className="vaCards"
                title={notificationsTitle}
                style={{ height: 169 }}
              >
                <Row gutter={[16, 16]}>
                  <Col
                    span={24}
                    style={{ alignContent: "center", textAlign: "center" }}
                  ></Col>
                </Row>
                <Row>
                  <Col
                    span={24}
                    style={{ alignContent: "center", textAlign: "center" }}
                  >
                    <p className="no-data-text">{"No Notifications"}</p>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};
export default Overview;
